import { fmt, fmtH, type PeriodInfo } from '@klukka/core';
import { Ic } from '@klukka/ui';
import { hoursOf, totals, type EmployeeCalc } from '../derive';
import { Panel, type Tr } from '../widgets';

/** The period report a payroll clerk hands on, plus a plain CSV export. */
export function ReportsTab({ all, pinfo, tr }: {
  all: EmployeeCalc[];
  pinfo: PeriodInfo;
  tr: Tr;
}) {
  const tot = totals(all);

  const exportCsv = () => {
    const rows = [['kennitala', 'nafn', 'dv', 'yv1', 'yv2', 'brutto', 'netto', 'kostnadur'].join(';')];
    for (const { emp, calc } of all) {
      rows.push([
        emp.kennitala, emp.name,
        calc.hours.dvH, calc.hours.yv1H, calc.hours.yv2H,
        Math.round(calc.gross), Math.round(calc.net), Math.round(calc.totalCost),
      ].join(';'));
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `klukka-${pinfo.start}_${pinfo.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Panel title={`${tr('report')} · ${pinfo.start} → ${pinfo.end}`}>
      <table className="tbl">
        <thead>
          <tr>
            <th>{tr('name')}</th><th>kt.</th><th>DV</th><th>YV1</th><th>YV2</th>
            <th>{tr('sickDaysCol')}</th><th>{tr('gross')}</th><th>{tr('net')}</th><th>{tr('cost')}</th>
          </tr>
        </thead>
        <tbody>
          {all.map(({ emp, calc }) => (
            <tr key={emp.id}>
              <td><b>{emp.name}</b></td>
              <td className="mono dimtx">{emp.kennitala}</td>
              <td className="mono">{fmtH(calc.hours.dvH)}</td>
              <td className="mono">{fmtH(calc.hours.yv1H)}</td>
              <td className="mono">{fmtH(calc.hours.yv2H)}</td>
              <td className="mono">{calc.rows.filter((r) => r.sick).length}</td>
              <td className="mono">{fmt(calc.gross)}</td>
              <td className="mono">{fmt(calc.net)}</td>
              <td className="mono"><b>{fmt(calc.totalCost)}</b></td>
            </tr>
          ))}
          <tr className="sum">
            <td colSpan={2}>{tr('total')}</td>
            <td className="mono">{fmtH(all.reduce((a, x) => a + x.calc.hours.dvH, 0))}</td>
            <td className="mono">{fmtH(all.reduce((a, x) => a + x.calc.hours.yv1H, 0))}</td>
            <td className="mono">{fmtH(all.reduce((a, x) => a + x.calc.hours.yv2H, 0))}</td>
            <td className="mono">{all.reduce((a, x) => a + x.calc.rows.filter((r) => r.sick).length, 0)}</td>
            <td className="mono">{fmt(tot.gross)}</td>
            <td className="mono">{fmt(tot.net)}</td>
            <td className="mono"><b>{fmt(tot.cost)}</b></td>
          </tr>
        </tbody>
      </table>

      <div className="dimtx" style={{ fontSize: 12, marginTop: 10 }}>
        {all.length} · {fmtH(all.reduce((a, x) => a + hoursOf(x.calc), 0))} klst
      </div>

      <button className="btn-sm" style={{ marginTop: 12 }} onClick={exportCsv}>
        <Ic.download size={14} /> {tr('export')}
      </button>
    </Panel>
  );
}
